import React from 'react';
import {
  WorkListRenderer,
  WorkDetailRenderer,
  WorkCreatedRenderer,
  WorkDashboardRenderer,
  WorkStandupRenderer,
  WorkDiffRenderer,
  WorkBurndownRenderer,
  WorkLeaderboardRenderer,
  WorkForecastRenderer,
} from './WorkRenderers';
import {
  AdminListRenderer,
  AdminDetailRenderer,
  AdminSuccessRenderer,
  AdminStatsRenderer,
} from './AdminRenderers';
import { JsonPreviewRenderer } from './JsonPreviewRenderer';
import { MapInlineRenderer } from './MapInlineRenderer';

// RendererProps + getToolName + getResultData live in `./_shared` to break
// a module-init cycle: index.ts -> MapInlineRenderer.tsx ->
// JsonPreviewRenderer.tsx -> _shared (cycle was: -> back to index.ts).
// Re-exported here so existing callers (WorkRenderers, AdminRenderers,
// AdminContextPanel) that imported these from `./index` keep working
// without a downstream change.
export { getResultData, getToolName } from './_shared';
export type { RendererProps } from './_shared';
import type { RendererProps } from './_shared';
import { getToolName } from './_shared';

export const TOOL_RENDERERS: Record<string, React.FC<RendererProps>> = {
  // Work tools
  admin_list_work_items: WorkListRenderer,
  admin_get_work_item: WorkDetailRenderer,
  admin_create_work_item: WorkCreatedRenderer,
  admin_update_work_item: WorkDetailRenderer,
  admin_delete_work_item: WorkDiffRenderer,
  admin_add_entity_ref: WorkDetailRenderer,
  admin_resolve_entity: WorkListRenderer,
  admin_scaffold_goal: WorkCreatedRenderer,
  admin_work_summary: WorkDashboardRenderer,
  admin_work_attention: WorkDashboardRenderer,
  admin_standup_summary: WorkStandupRenderer,
  admin_reassign_tasks: WorkDiffRenderer,
  admin_bulk_update_status: WorkDiffRenderer,
  admin_work_burndown: WorkBurndownRenderer,
  admin_work_leaderboard: WorkLeaderboardRenderer,
  admin_work_forecast: WorkForecastRenderer,

  // Admin user/system tools
  admin_list_users: AdminListRenderer,
  admin_get_user_detail: AdminDetailRenderer,
  admin_create_user: AdminSuccessRenderer,
  admin_change_user_status: AdminSuccessRenderer,
  admin_reset_password: AdminSuccessRenderer,
  admin_assign_role: AdminSuccessRenderer,
  admin_invite_user: AdminSuccessRenderer,
  admin_list_invites: AdminListRenderer,
  admin_revoke_invite: AdminSuccessRenderer,
  admin_list_templates: AdminListRenderer,
  admin_create_template: AdminSuccessRenderer,
  admin_get_system_stats: AdminStatsRenderer,
  admin_view_audit_log: AdminListRenderer,
  admin_get_version: AdminDetailRenderer,
  admin_summarize_audit_log: AdminDetailRenderer,
  admin_get_platform_info: AdminDetailRenderer,
  admin_browse_api: AdminListRenderer,
  admin_count_entities: AdminStatsRenderer,
};

/** Renderer dispatch by `default_renderer` hint from ToolMetadata.
 *  When the backend's tool_docs(action='list') response carries a
 *  default_renderer ('map' or 'json'), the chat panel uses this map
 *  to pick the appropriate renderer regardless of the tool's name. */
export const RENDERERS_BY_HINT: Record<string, React.FC<RendererProps>> = {
  map: MapInlineRenderer as unknown as React.FC<RendererProps>,
  json: JsonPreviewRenderer,
};

/** Extended dispatch — preferred for new call sites that have the
 *  tool's metadata available. Falls back to legacy tool-name lookup
 *  when no hint is present, then to JsonPreviewRenderer. */
export function getRenderer(
  toolCall: any,
  toolMetadata?: { default_renderer?: string | null },
): React.FC<RendererProps> {
  const hint = toolMetadata?.default_renderer;
  if (hint && RENDERERS_BY_HINT[hint]) {
    return RENDERERS_BY_HINT[hint];
  }
  const toolName = getToolName(toolCall);
  return TOOL_RENDERERS[toolName] ?? JsonPreviewRenderer;
}

/** Legacy single-arg dispatch — kept for AdminContextPanel and any other
 *  callers that don't yet have tool metadata reachable. */
export function getRendererByName(toolName: string): React.FC<RendererProps> {
  return TOOL_RENDERERS[toolName] ?? JsonPreviewRenderer;
}

export { JsonPreviewRenderer };
